/* Precision Atelier protocol build: route architecture makes glass, insulation, local, trust, and contact content indexable. */
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import { lazy, Suspense, type ReactNode } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import NotFound from "./pages/NotFound";
import { Home } from "./pages/HomeCore";

const Areas = lazy(() => import("./pages/Home").then(module => ({ default: module.Areas })));
const Blog = lazy(() => import("./pages/Home").then(module => ({ default: module.Blog })));
const Contact = lazy(() => import("./pages/Home").then(module => ({ default: module.Contact })));
const FAQs = lazy(() => import("./pages/Home").then(module => ({ default: module.FAQs })));
const Gallery = lazy(() => import("./pages/Home").then(module => ({ default: module.Gallery })));
const GuidePage = lazy(() => import("./pages/Home").then(module => ({ default: module.GuidePage })));
const LeadDashboard = lazy(() => import("./pages/LeadDashboard"));
const LocationPage = lazy(() => import("./pages/Home").then(module => ({ default: module.LocationPage })));
const Reviews = lazy(() => import("./pages/Home").then(module => ({ default: module.Reviews })));
const ServiceHub = lazy(() => import("./pages/Home").then(module => ({ default: module.ServiceHub })));
const ServicePage = lazy(() => import("./pages/Home").then(module => ({ default: module.ServicePage })));

function DeferredRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

const servicePaths = [
  "/glass-services/emergency-glass-repair/", "/glass-services/shower-doors/", "/glass-services/storefront-commercial-glass/", "/glass-services/window-glass-replacement/", "/glass-services/mirrors-railings-partitions/",
  "/insulation-services/spray-foam/", "/insulation-services/attic-insulation/", "/insulation-services/blown-in-batt/", "/insulation-services/commercial-insulation/", "/insulation-services/mass-save-rebates/",
];

const areaPaths = [
  ["boston", "Boston"], ["brookline", "Brookline"], ["cambridge", "Cambridge"],
  ["haverhill", "Haverhill"], ["lowell", "Lowell"], ["lawrence", "Lawrence"],
  ["newton", "Newton"], ["wellesley", "Wellesley"], ["worcester", "Worcester"],
] as const;
function Router() {
  // make sure to consider if you need authentication for certain routes
  return <Switch>
    <Route path="/" component={Home} />
    <Route path="/glass-services/" component={() => <DeferredRoute><ServiceHub type="glass" /></DeferredRoute>} />
    <Route path="/insulation-services/" component={() => <DeferredRoute><ServiceHub type="insulation" /></DeferredRoute>} />
    {servicePaths.map(path => <Route key={path} path={path} component={() => <DeferredRoute><ServicePage path={path} /></DeferredRoute>} />)}
    <Route path="/service-areas/" component={() => <DeferredRoute><Areas /></DeferredRoute>} />
    {areaPaths.map(([slug, city]) => <Route key={slug} path={`/service-areas/${slug}/`} component={() => <DeferredRoute><LocationPage city={city} /></DeferredRoute>} />)}
    <Route path="/gallery/" component={() => <DeferredRoute><Gallery /></DeferredRoute>} />
    <Route path="/reviews/" component={() => <DeferredRoute><Reviews /></DeferredRoute>} />
    <Route path="/faqs/" component={() => <DeferredRoute><FAQs /></DeferredRoute>} />
    <Route path="/contact/" component={() => <DeferredRoute><Contact /></DeferredRoute>} />
    <Route path="/request-quote/" component={() => <DeferredRoute><Contact /></DeferredRoute>} />
    <Route path="/blog/" component={() => <DeferredRoute><Blog /></DeferredRoute>} />
    <Route path="/guides/mass-save-insulation-questions/" component={() => <DeferredRoute><GuidePage slug="mass-save-insulation-questions" /></DeferredRoute>} />
    <Route path="/guides/foggy-glass-or-insulation/" component={() => <DeferredRoute><GuidePage slug="foggy-glass-or-insulation" /></DeferredRoute>} />
    <Route path="/guides/broken-storefront-window/" component={() => <DeferredRoute><GuidePage slug="broken-storefront-window" /></DeferredRoute>} />
    <Route path="/owner/leads/" component={() => <DeferredRoute><LeadDashboard /></DeferredRoute>} />
    <Route path="/404" component={NotFound} />
    <Route component={NotFound} />
  </Switch>;
}

export default function App() {
  return <ErrorBoundary><ThemeProvider defaultTheme="light"><TooltipProvider><Toaster /><Router /></TooltipProvider></ThemeProvider></ErrorBoundary>;
}
